import { useRef } from "react";
import "./CoachMessageModal.css";
import { useModalA11y } from "./useModalA11y";
import { TALK_TO_SOMEONE_URL, asset } from "../config";
import { track } from "../analytics/posthog";
import { useI18n } from "../i18n";

/**
 * Instructions popup for messaging the WorkWell coach through Joy (copy to
 * Yvett's spec — structure 14 Jul, wording updated 17 Jul).
 *
 * Joy needs the resident to be logged in, and after login it lands them on its
 * own home page rather than the message box. So: step 1 is a plain blue link
 * that opens Joy for account creation / login, step 2 sends them back here,
 * and step 3 holds the "Send a Message to WorkWell Coach" button, which now
 * goes straight to the message page. The only bottom action is "Not now" (the
 * primary action lives in step 3, not the footer).
 */
export function CoachMessageModal({ onClose }: { onClose: () => void }) {
  const { t } = useI18n();
  const dialogRef = useRef<HTMLDivElement>(null);
  useModalA11y(dialogRef, onClose);

  return (
    <div
      className="modal-overlay"
      role="presentation"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        ref={dialogRef}
        className="modal coach-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="coach-modal-title"
      >
        <h2 className="modal__title" id="coach-modal-title">
          {t("coach.title")}
        </h2>
        <p className="modal__intro">
          Direct messaging to the coach is handled by <strong>Joy</strong>, an NHS-partnered
          digital care platform. Follow these steps to send your message.
        </p>

        {/* PROJ-115. Safeguarding, not decoration: this message leaves WIRE for
            Joy, so WIRE's crisis detection does not see it, and the roving
            coaches are not staffed 24/7 (Glenn, 18 Aug). A resident must not
            read the message box as a helpline. Deliberately no SLA figure —
            none is agreed, and a number we cannot keep is worse than none. */}
        <p className="coach-notice" role="note">
          {t("coach.notice")}{" "}
          <strong>{t("coach.notice.urgent")}</strong>
        </p>

        <ol className="coach-steps">
          <li className="coach-step">
            <span className="coach-step__num">1</span>
            <div className="coach-step__body">
              {/* Yvett's copy puts the link on "here"; the accessible name spells
                  out the destination so it isn't announced as a bare "here". */}
              Create a Joy account or log in{" "}
              <a
                className="coach-step__link"
                href={TALK_TO_SOMEONE_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="here — create a Joy account or log in (opens in a new tab)"
                onClick={() => track("human_help_clicked", { source: "coach_joy_login_link" })}
              >
                here
              </a>
              <img
                className="coach-step__shot"
                src={asset("joy/joy-login-page.png")}
                alt="The Joy login page, with Email and Password fields, a Log in button, and links to create a new account or reset a forgotten password."
              />
            </div>
          </li>
          <li className="coach-step">
            <span className="coach-step__num">2</span>
            <div className="coach-step__body">
              <strong>Close the Joy tab</strong> and come back to this chat.
            </div>
          </li>
          <li className="coach-step">
            <span className="coach-step__num">3</span>
            <div className="coach-step__body">
              Click on the button below to go to the messaging page directly.
              <a
                className="coach-step__button"
                href={TALK_TO_SOMEONE_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track("human_help_clicked", { source: "coach_joy_message_button" })}
              >
                Send a Message to WorkWell Coach
              </a>
            </div>
          </li>
          <li className="coach-step">
            <span className="coach-step__num">4</span>
            <div className="coach-step__body">
              <strong>Write your message and send it.</strong> A coach will get in touch.
              <img
                className="coach-step__shot"
                src={asset("joy/joy-message-page.png")}
                alt="The Joy 'Send message to LLR Roving Workwell' page, with a message box and a Send message button."
              />
            </div>
          </li>
        </ol>

        <div className="coach-modal__actions">
          <button type="button" className="coach-modal__cancel" onClick={onClose}>
            {t("coach.notNow")}
          </button>
        </div>
      </div>
    </div>
  );
}
